import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import PageHeader from './_components/PageHeader';
/* ---------- Icons ---------- */

const I = ({ d, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BellIcon       = (p) => <I {...p} d="M12 3a5 5 0 00-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 00-5-5zM10 18a2 2 0 004 0" />;
const SearchIcon     = (p) => <I {...p} d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-3.5-3.5" />;
const ChatIcon       = (p) => <I {...p} d="M4 5h16v11H8l-4 4V5z" />;
const CheckIcon      = (p) => <I {...p} d="M5 12l4 4 10-10" />;
const ArrowUpRight   = (p) => <I {...p} d="M7 17L17 7M9 7h8v8" />;
const ArrowDownLeft  = (p) => <I {...p} d="M17 7L7 17M15 17H7V9" />;
const ChevronDown    = (p) => <I {...p} d="M6 9l6 6 6-6" />;

/* ---------- Helpers ---------- */

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDateLong = (d = new Date()) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const fmtDateShort = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const last4 = (masked) => {
  if (!masked) return '';
  const digits = String(masked).replace(/\D/g, '');
  return digits.slice(-4);
};

/* ---------- Filter logic ---------- */

const FILTERS = [
  { value: 'all',    label: 'All activity' },
  { value: '7d',     label: 'Last 7 days' },
  { value: '30d',    label: 'Last 30 days' },
  { value: '90d',    label: 'Last 90 days' },
  { value: 'year',   label: 'This year' },
];

const filterTransactions = (txs, filter) => {
  if (filter === 'all') return txs;
  const now = new Date();
  const cutoff = new Date();
  if (filter === '7d') cutoff.setDate(now.getDate() - 7);
  else if (filter === '30d') cutoff.setDate(now.getDate() - 30);
  else if (filter === '90d') cutoff.setDate(now.getDate() - 90);
  else if (filter === 'year') cutoff.setMonth(0, 1), cutoff.setHours(0, 0, 0, 0);

  return txs.filter((t) => t.paymentDate && new Date(t.paymentDate) >= cutoff);
};

/* ---------- Page header (same as Accounts) ---------- */



/* ---------- Transaction row ---------- */

const TransactionRow = ({ tx, accountSuffix }) => {
  const isIncoming = (tx.direction || 'in') === 'in';
  const statusLabel = (tx.status || 'Pending').toUpperCase();
  const amount = tx.amountPaid || 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4">
      {/* Direction icon */}
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          isIncoming ? 'bg-[#dff3e4] text-[#1f5c3a]' : 'bg-red-50 text-red-600'
        }`}
      >
        {isIncoming ? (
          <ArrowUpRight className="w-5 h-5" />
        ) : (
          <ArrowDownLeft className="w-5 h-5" />
        )}
      </div>

      {/* Middle: name + date · suffix */}
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-bold text-[#1a2b3c] truncate">
          {tx.name || tx.methodOfPayment || 'Transaction'}
        </div>
        <div className="text-[12px] text-gray-500 mt-0.5">
          {fmtDateShort(tx.paymentDate)}
          {accountSuffix ? ` · ${accountSuffix}` : ''}
        </div>
      </div>

      {/* Right: amount + status pill */}
      <div className="text-right shrink-0">
        <div
          className={`text-[15px] font-bold ${
            isIncoming ? 'text-[#1f5c3a]' : 'text-red-600'
          }`}
        >
          {isIncoming ? '+' : '-'}
          {fmtMoney(Math.abs(amount))}
        </div>
        <div className="mt-1">
          <span className="inline-block bg-gray-100 text-gray-600 text-[10px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full">
            {statusLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ---------- Empty state ---------- */

const EmptyState = () => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
    <div className="text-[14px] font-semibold text-[#1a2b3c]">
      No transactions yet
    </div>
    <p className="text-[12px] text-gray-500 mt-1">
      Transactions will appear here as activity is recorded.
    </p>
  </div>
);

/* ---------- Page ---------- */

const FintechTransactions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/fintech/dashboard');
        if (alive) setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const holderName = data?.accountHolderName || '';
  const accountSuffix = last4(data?.linkedAccounts?.[0]?.accountNumberMasked);

  const allTransactions = data?.recentTransactions || [];
  const transactions = useMemo(
    () => filterTransactions(allTransactions, filter),
    [allTransactions, filter]
  );

  return (
    <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-3xl mx-auto">
      <PageHeader holderName={holderName} />

      {/* Title */}
      <div className="mb-5">
        <h2 className="font-serif text-[24px] leading-tight text-[#1a2b3c]">
          Transaction History
        </h2>
        <p className="text-[13px] text-gray-500 mt-1">All account activity</p>
      </div>

      {/* Filter */}
      <div className="mb-5 relative">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 rounded-xl pl-4 pr-10 py-3 text-[13px] text-[#1a2b3c] focus:outline-none focus:ring-2 focus:ring-[#0b2740]/20 focus:border-[#0b2740]"
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
          <ChevronDown className="w-4 h-4" />
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 flex items-center gap-4 animate-pulse">
              <div className="w-11 h-11 rounded-xl bg-gray-200 shrink-0" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 rounded" />
                <div className="h-3 w-24 bg-gray-200 rounded mt-2" />
              </div>
              <div className="text-right">
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded mt-2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {!loading && transactions.length === 0 && <EmptyState />}

      {!loading && transactions.length > 0 && (
        <div className="space-y-3">
          {transactions.map((tx) => (
            <TransactionRow
              key={tx.trackingNumber}
              tx={tx}
              accountSuffix={accountSuffix}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FintechTransactions;